<script setup lang="ts">
import type { DropdownMenuItem } from '@nuxt/ui'
import type { CustomerUsageSeries } from '@/types'
import { Deferred, Head } from '@inertiajs/vue3'
import { computed } from 'vue'
import BarChart from '@/components/charts/BarChart.vue'
import ChartPanel from '@/components/charts/ChartPanel.vue'
import TimeRangeToolbar from '@/components/charts/TimeRangeToolbar.vue'
import TimeSeriesChart from '@/components/charts/TimeSeriesChart.vue'
import DashboardStats from '@/components/dashboard/home/DashboardStats.vue'
import { sumUsagePoints, totalUsagePoints, useCustomerUsageChartSeries } from '@/composables/useCustomerUsageChartSeries'
import { provideTimeRange } from '@/composables/useTimeRange'
import { dashboardLayout } from '@/lib/layouts'
import { formatCount } from '@/lib/numbers'
import { newAdminCustomerPath, newAdminProductPath } from '@/routes'

defineOptions({
  layout: dashboardLayout,
})

const props = defineProps<{
  productCount: number
  customerCount: number
  activeInstanceCount: number
  usageEventsThisMonth: number
  customerUsageSeries?: CustomerUsageSeries
  dailyUsageSeries?: CustomerUsageSeries
  monthlyUsageSeries?: CustomerUsageSeries
}>()

const timeRange = provideTimeRange({ only: ['customerUsageSeries'] })

const items = [
  [
    {
      label: 'New product',
      icon: 'i-lucide-box',
      to: newAdminProductPath(),
    },
    {
      label: 'New customer',
      icon: 'i-lucide-user-plus',
      to: newAdminCustomerPath(),
    },
  ],
] satisfies DropdownMenuItem[][]

const title = 'Dashboard'

const { chartSeries: usageSeries, total: usageTotal } = useCustomerUsageChartSeries(
  () => props.customerUsageSeries,
  'Total events',
)

const dailyPoints = computed(() => totalUsagePoints(props.dailyUsageSeries))
const monthlyPoints = computed(() => totalUsagePoints(props.monthlyUsageSeries))
const dailyTotal = computed(() => sumUsagePoints(dailyPoints.value))
const monthlyTotal = computed(() => sumUsagePoints(monthlyPoints.value))
</script>

<template>
  <Head :title="title" />

  <UDashboardPanel id="home">
    <template #header>
      <UDashboardNavbar :title="title" :ui="{ right: 'gap-3' }">
        <template #leading>
          <UDashboardSidebarCollapse />
        </template>

        <template #right>
          <TimeRangeToolbar :controller="timeRange" />
          <UDropdownMenu :items="items">
            <UButton icon="i-lucide-plus" size="md" class="rounded-full" />
          </UDropdownMenu>
        </template>
      </UDashboardNavbar>
    </template>

    <template #body>
      <DashboardStats
        :customers="customerCount"
        :products="productCount"
        :active-instances="activeInstanceCount"
        :usage-events-this-month="usageEventsThisMonth"
      />

      <ChartPanel
        title="Billable usage"
        :subtitle="`${formatCount(usageTotal)} events over range · current product prices are invoice export hints`"
        :loading="timeRange.loading.value"
      >
        <Deferred :data="['customerUsageSeries']">
          <template #fallback>
            <USkeleton class="h-80 w-full" />
          </template>
          <TimeSeriesChart
            :series="usageSeries"
            :resolved="timeRange.resolved.value"
            :height="320"
            :controller="timeRange"
            :value-format="(value) => formatCount(value)"
          />
        </Deferred>
      </ChartPanel>

      <div class="grid gap-8 lg:grid-cols-2">
        <ChartPanel title="Daily usage events" subtitle="Last 30 days">
          <Deferred :data="['dailyUsageSeries']">
            <template #fallback>
              <USkeleton class="h-56 w-full" />
            </template>
            <p class="text-highlighted mb-2 text-2xl font-semibold tabular-nums">
              {{ formatCount(dailyTotal) }}
            </p>
            <BarChart :points="dailyPoints" granularity="day" :height="220" :value-format="(value) => formatCount(value)" />
          </Deferred>
        </ChartPanel>

        <ChartPanel title="Monthly usage events" subtitle="Last 12 months">
          <Deferred :data="['monthlyUsageSeries']">
            <template #fallback>
              <USkeleton class="h-56 w-full" />
            </template>
            <p class="text-highlighted mb-2 text-2xl font-semibold tabular-nums">
              {{ formatCount(monthlyTotal) }}
            </p>
            <BarChart :points="monthlyPoints" granularity="month" :height="220" :value-format="(value) => formatCount(value)" />
          </Deferred>
        </ChartPanel>
      </div>
    </template>
  </UDashboardPanel>
</template>
